import { supabase } from './supabase';
import toast from 'react-hot-toast';

export interface SignUpData {
  email: string;
  password: string;
}

export interface SignInData {
  email: string;
  password: string;
}

export const signUp = async ({ email, password }: SignUpData) => {
  // Use the correct Vite environment variable format
  const redirectUrl = import.meta.env.VITE_AUTH_REDIRECT_URL || `${window.location.origin}/auth/callback`;
  
  console.log('Using signup redirect URL:', redirectUrl); // Debugging
  
  // Maximum number of retry attempts (reduced following best practices)
  const MAX_RETRIES = 3;
  // Initial delay between retries (increased to 5 seconds to avoid rate limiting)
  const INITIAL_DELAY = 5000;
  // Jitter factor to add randomness to retry timing (prevents clustered retries)
  const JITTER_FACTOR = 0.2; // 20% variance
  
  let lastError = null;
  let retryCount = 0;
  
  // No test bypass mechanism in production
  
  while (retryCount < MAX_RETRIES) {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redirectUrl
        }
      });
      
      if (error) {
        console.error('Signup error details:', {
          message: error.message,
          status: error.status,
          name: error.name
        });
        
        // Log email service errors for monitoring
        if (error.message && (
           error.message.includes('Too many concurrent SMTP connections') ||
           error.message.includes('Error sending confirmation email') ||
           error.message.includes('rate limit') ||
           error.message.includes('email') ||
           error.message.toLowerCase().includes('smtp') ||
           error.message.includes('timeout') ||
           error.message.includes('deadline exceeded'))) {
          
          console.error('Email service error detected:', error.message);
        }
        
        // If it's an SMTP rate limiting error or any email-related error, retry after a delay
        if (error.message && (
           error.message.includes('Too many concurrent SMTP connections') ||
           error.message.includes('Error sending confirmation email') ||
           error.message.includes('rate limit') ||
           error.message.includes('email') ||
           error.message.toLowerCase().includes('smtp') ||
           error.message.includes('timeout') ||
           error.message.includes('deadline exceeded'))) {
          
          // Increment retry count
          retryCount++;
          
          if (retryCount < MAX_RETRIES) {
            // Exponential backoff with jitter: delay increases with each retry attempt
            const baseDelay = INITIAL_DELAY * Math.pow(2, retryCount);
            // Add jitter to prevent thundering herd problem
            const jitter = baseDelay * JITTER_FACTOR * (Math.random() * 2 - 1); // Random value between -20% and +20%
            const delay = Math.max(baseDelay + jitter, INITIAL_DELAY); // Ensure minimum delay
            
            console.log(`SMTP rate limit hit, retrying in ${delay/1000} seconds (attempt ${retryCount}/${MAX_RETRIES})`);
            
            // Wait before trying again
            await new Promise(resolve => setTimeout(resolve, delay));
            
            // Save the error in case all retries fail
            lastError = error;
            
            // Continue to next retry attempt
            continue;
          }
        }
        
        // For non-retryable errors or if max retries reached, throw immediately
        throw error;
      }
      
      // If successful, return the data
      return data;
      
    } catch (error) {
      // For unexpected errors during the process
      if (retryCount < MAX_RETRIES - 1) {
        console.error(`Unexpected error during signup, retrying: ${error}`);
        retryCount++;
        // Exponential backoff with jitter for unexpected errors
        const baseDelay = INITIAL_DELAY * Math.pow(2, retryCount);
        const jitter = baseDelay * JITTER_FACTOR * (Math.random() * 2 - 1);
        const delay = Math.max(baseDelay + jitter, INITIAL_DELAY);
        await new Promise(resolve => setTimeout(resolve, delay));
        lastError = error;
      } else {
        throw error;
      }
    }
  }
  
  // If we've exhausted all retries
  throw lastError || new Error('Maximum retry attempts reached');
};

export const signIn = async ({ email, password }: SignInData) => {
  try {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      // Provide more specific error messages
      if (error.message === 'Invalid login credentials') {
        throw new Error('Invalid email or password. Please check your credentials and try again.');
      }
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Sign in error:', error);
    throw error;
  }
};

export const signOut = async () => {
  try {
    const { error } = await supabase.auth.signOut();
    
    if (error) {
      console.error('Error signing out:', error);
      toast.error('Failed to sign out. Please try again.');
      throw error;
    }

    // Clear all Supabase-related storage
    for (const key of Object.keys(localStorage)) {
      if (key.startsWith('supabase.')) {
        localStorage.removeItem(key);
      }
    }
    
    // Force reload to clear any cached auth state
    window.location.replace('/');

  } catch (error) {
    console.error('Error signing out:', error);
    toast.error('Failed to sign out. Please try again.');
    throw error;
  }
};

export const getCurrentUser = async () => {
  const { data: { user }, error } = await supabase.auth.getUser();
  
  if (error) {
    throw error;
  }
  
  return user;
};

export const resetPassword = async (email: string): Promise<{ error: Error | null }> => {
  try {
    // Use consistent redirect URL format for password reset
    const baseRedirectUrl = import.meta.env.VITE_AUTH_REDIRECT_URL || `${window.location.origin}/auth/callback`;
    // Extract the base URL without the path
    const baseUrl = baseRedirectUrl.split('/auth/')[0];
    const resetRedirectUrl = `${baseUrl}/auth/reset-password`;
    
    console.log('Using reset redirect URL:', resetRedirectUrl); // Debugging
    
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: resetRedirectUrl
    });

    if (error) {
      console.error('Supabase reset error:', error);
      return { 
        error: new Error('Unable to send password reset email. Please try again later.')
      };
    }

    return { error: null };
  } catch (error) {
    console.error('Password reset error:', error);
    return { 
      error: new Error('Unable to send password reset email. Please try again later or contact support.')
    };
  }
};

export const updatePassword = async (newPassword: string): Promise<{ error: Error | null }> => {
  try {
    const { error } = await supabase.auth.updateUser({
      password: newPassword
    });

    if (error) throw error;
    return { error: null };
  } catch (error) {
    console.error('Password update error:', error);
    return { error: error as Error };
  }
};

/**
 * Check if the user is currently authenticated
 * Used to restrict access to subscription-only content
 * @returns {Promise<boolean>} Whether the user is authenticated
 */
export const getIsAuthenticated = async (): Promise<boolean> => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    return !!session?.user;
  } catch (error) {
    console.error('Authentication check error:', error);
    return false;
  }
};